import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Search, ShoppingBag, Menu, X, Heart, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { api } from "@/lib/api";
import { WOMEN_SLUGS, MEN_SLUGS, pickCategories } from "@/data/homeCategories";
import InstallAppButton from "@/components/InstallAppButton";
import { metaSearch } from "@/lib/metaPixel";
import { openaiSearch } from "@/lib/openaiPixel";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import SmartImage from "@/components/SmartImage";

const ASK_AI_DISMISSED_KEY = "lux_ask_ai_seen";

/**
 * Myntra-inspired header:
 *  - White background, sticky
 *  - Bold wordmark logo left
 *  - Category ribbon (MEN | WOMEN | KIDS | HOME | BEAUTY | STUDIO)
 *    with pink underline on active/hover
 *  - Full-width pink-bordered search on desktop
 *  - Icons on right: Profile / Wishlist / Bag
 */

const CAT_TABS = [
  { label: "Men", to: "/collections/men", testId: "nav-men" },
  { label: "Women", to: "/collections/women", testId: "nav-women" },
  { label: "Fragrance", to: "/category/h-perfumes", testId: "nav-fragrance" },
  { label: "Jewellery", to: "/category/jewellery", testId: "nav-jewellery" },
  { label: "Reviews", to: "/reviews", testId: "nav-reviews" },
  { label: "Source it", to: "/source-on-demand", testId: "nav-source", isNew: true },
];

const Header = ({ categories = [] }) => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { count: wishlistCount } = useWishlist();
  const [menuOpen, setMenuOpen] = useState(false);
  const [askAiSeen, setAskAiSeen] = useState(() => {
    try { return localStorage.getItem(ASK_AI_DISMISSED_KEY) === "1"; }
    catch (_e) { return true; }
  });
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    // Refresh the recently-viewed strip every time the drawer opens so
    // it reflects the very latest visit.
    if (menuOpen) setRecent(getRecentlyViewed().slice(0, 5));
  }, [menuOpen]);
  const dismissAskAi = () => {
    setAskAiSeen(true);
    try { localStorage.setItem(ASK_AI_DISMISSED_KEY, "1"); }
    catch (_e) {}
  };
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      inputRef.current?.focus();
    };
    window.addEventListener("luxsova:open-search", onOpen);
    return () => window.removeEventListener("luxsova:open-search", onOpen);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!q || q.length < 3) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.search(q, 8);
        setResults(r.items || []);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setMenuOpen(false);
    setResults([]);
    setSearchFocused(false);
    metaSearch(q.trim());
    openaiSearch(q.trim());
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const women = pickCategories(categories, WOMEN_SLUGS);
  const men = pickCategories(categories, MEN_SLUGS);

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-line shadow-sm lux-safe-top"
      data-testid="site-header"
    >
      <div className="lux-container lux-safe-x">
        <div className="flex items-center gap-4 md:gap-8 h-14 md:h-20">
          {/* Logo — left aligned, prominent on mobile */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="md:hidden -ml-1.5 p-1.5 hover:bg-neutral-100 rounded transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-ink" strokeWidth={2} />
          </button>
          <Link to="/" className="flex-shrink-0" data-testid="site-logo">
            <span
              className="text-2xl md:text-3xl font-extrabold tracking-tight"
              style={{ color: "#282c3f" }}
            >
              <span style={{ color: "#ff3f6c" }}>lux</span>sova
            </span>
          </Link>

          {/* Desktop category tabs */}
          <nav className="hidden md:flex items-center gap-8 flex-shrink-0">
            {CAT_TABS.map((c) => (
              <Link
                key={c.testId}
                to={c.to}
                className="nav-link relative"
                data-testid={c.testId}
              >
                {c.label}
                {c.isNew && (
                  <span className="absolute -top-1 -right-4 text-[9px] font-bold text-pink">
                    NEW
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Search — Myntra style */}
          <div className="flex-1 hidden md:block relative">
            <form onSubmit={submitSearch}>
              <div className="relative">
                <Search className="w-4 h-4 text-ink-mute absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  placeholder="Search for products, brands and more"
                  className="w-full h-10 bg-bg-2 border border-transparent hover:border-line-strong rounded px-10 text-sm placeholder:text-ink-mute focus:outline-none focus:border-pink focus:bg-white transition"
                  data-testid="search-input"
                  autoComplete="off"
                  enterKeyHint="search"
                  style={{ backgroundColor: "#f5f5f6" }}
                />
              </div>
            </form>
            {searchFocused && q.length >= 3 && (
              <div className="absolute top-12 left-0 right-0 bg-white border border-line rounded shadow-lg max-h-[420px] overflow-y-auto z-50">
                {searching && (
                  <p className="text-xs text-ink-mute p-4">Searching…</p>
                )}
                {!searching && results.length === 0 && (
                  <p className="text-xs text-ink-mute p-4" data-testid="search-no-results">
                    No matches — try a different keyword.
                  </p>
                )}
                <ul>
                  {results.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/product/${r.slug}`}
                        onMouseDown={() => setSearchFocused(false)}
                        className="flex gap-3 items-center p-3 hover:bg-bg-2 transition"
                        data-testid={`search-suggestion-${r.slug}`}
                      >
                        <div className="w-12 h-12 bg-bg-2 overflow-hidden flex-shrink-0">
                          {r.image && (
                            <img
                              src={r.image}
                              alt={r.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                        <div className="text-xs leading-tight line-clamp-2 text-ink">
                          {r.name}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right icons — mobile shows only Ask-AI + Wishlist + Bag (Profile lives in bottom nav) */}
          <div className="flex items-center gap-3 md:gap-6 flex-shrink-0 ml-auto md:ml-0">
            <button
              type="button"
              onClick={() => {
                dismissAskAi();
                window.dispatchEvent(new CustomEvent("luxsova:open-shopper"));
              }}
              className="md:hidden relative flex items-center gap-1 px-2 py-1 rounded-full border border-pink text-pink hover:bg-pink hover:text-white transition-colors"
              data-testid="mobile-ask-ai"
              aria-label="Ask AI"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.9 4.2L18 9l-4.1 1.8L12 15l-1.9-4.2L6 9l4.1-1.8z" />
                <path d="M19 15l.8 1.8L22 18l-2.2 1.2L19 21l-.8-1.8L16 18l2.2-1.2z" />
              </svg>
              <span className="text-[10px] font-bold uppercase tracking-wider">Ask AI</span>
              {!askAiSeen && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-pink rounded-full animate-ping"
                  data-testid="mobile-ask-ai-dot-ping"
                />
              )}
              {!askAiSeen && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-pink rounded-full"
                  data-testid="mobile-ask-ai-dot"
                />
              )}
            </button>
            <Link
              to={user ? (["admin", "staff"].includes(user.role) ? "/admin" : "/account") : "/login"}
              className="hidden md:flex flex-col items-center hover:text-pink transition-colors p-1"
              data-testid="header-account"
            >
              <User className="w-5 h-5" strokeWidth={1.75} />
              <span className="hidden md:block text-[11px] font-bold mt-0.5">Profile</span>
            </Link>
            <Link
              to="/wishlist"
              className="flex flex-col items-center hover:text-pink transition-colors p-1 relative"
              data-testid="header-wishlist"
              aria-label={`Wishlist${wishlistCount > 0 ? ` — ${wishlistCount} saved` : ""}`}
            >
              <Heart
                className={`w-[22px] h-[22px] md:w-5 md:h-5 ${wishlistCount > 0 ? "text-pink" : ""}`}
                strokeWidth={1.75}
                fill={wishlistCount > 0 ? "currentColor" : "none"}
              />
              <span className="hidden md:block text-[11px] font-bold mt-0.5">Wishlist</span>
              {wishlistCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 md:top-0 md:right-2 bg-pink text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                  data-testid="wishlist-count"
                >
                  {wishlistCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setOpen(true)}
              className="flex flex-col items-center hover:text-pink transition-colors p-1 relative"
              data-testid="open-cart"
              aria-label="Cart"
            >
              <ShoppingBag className="w-[22px] h-[22px] md:w-5 md:h-5" strokeWidth={1.75} />
              <span className="hidden md:block text-[11px] font-bold mt-0.5">Bag</span>
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 md:top-0 md:right-2 bg-pink text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile search — with mic icon (Myntra style) */}
        <div className="md:hidden pb-2.5 pt-1">
          <form onSubmit={submitSearch}>
            <div className="relative">
              <Search className="w-4 h-4 text-pink absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={2.5} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for products, brands and more"
                className="w-full h-11 bg-bg-2 border border-transparent focus:border-pink rounded pl-10 pr-11 text-[13px] placeholder:text-ink-mute focus:outline-none focus:bg-white transition"
                data-testid="mobile-search-input"
                autoComplete="off"
                enterKeyHint="search"
                style={{ backgroundColor: "#f5f5f6" }}
              />
              <button
                type="button"
                aria-label="Voice search"
                data-testid="mobile-search-mic"
                onClick={() => {
                  /* Voice search hook — falls back to focusing the input on
                     browsers without SpeechRecognition (Safari iOS <17). */
                  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
                  if (!SR) {
                    inputRef.current?.focus();
                    return;
                  }
                  const r = new SR();
                  r.lang = "en-IN";
                  r.onresult = (e) => setQ(e.results[0][0].transcript);
                  r.start();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-pink"
              >
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0M12 17v4M8 21h8" />
                </svg>
              </button>
            </div>
          </form>
        </div>

        {/* Mobile sticky category tabs — Myntra ribbon */}
        <div className="md:hidden -mx-4 border-t border-line">
          <div className="flex overflow-x-auto no-scrollbar px-4">
            {CAT_TABS.map((c) => (
              <Link
                key={`m-${c.testId}`}
                to={c.to}
                className="relative flex-shrink-0 px-4 py-3 text-[12px] font-bold uppercase tracking-wider text-ink hover:text-pink"
                data-testid={`mobile-${c.testId}`}
              >
                {c.label}
                {c.isNew && (
                  <span className="absolute -top-0.5 -right-0 text-[8px] font-bold text-pink">
                    NEW
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[90] md:hidden lux-safe-top bg-white"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between p-5 border-b border-line bg-white">
            <span className="text-2xl font-extrabold tracking-tight">
              <span className="text-pink">lux</span>sova
            </span>
            <button
              onClick={() => setMenuOpen(false)}
              data-testid="close-mobile-menu"
              aria-label="Close menu"
              className="p-2 -mr-2"
            >
              <X className="w-6 h-6 text-ink" />
            </button>
          </div>
          <div className="overflow-y-auto h-[calc(100vh-72px)] px-5 pb-32 bg-white">
            <div className="flex flex-wrap gap-2 py-4 border-b border-line">
              <Link
                to={user ? (["admin", "staff"].includes(user.role) ? "/admin" : "/account") : "/login"}
                onClick={() => setMenuOpen(false)}
                className="btn-outline text-xs !py-2 !px-4"
                data-testid="mobile-account-link"
              >
                {user ? "Account" : "Sign in"}
              </Link>
              <Link
                to="/wishlist"
                onClick={() => setMenuOpen(false)}
                className="btn-outline text-xs !py-2 !px-4"
              >
                Wishlist
              </Link>
              <Link
                to="/source-on-demand"
                onClick={() => setMenuOpen(false)}
                className="btn-outline text-xs !py-2 !px-4"
                data-testid="mobile-source-link"
              >
                Source it
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  dismissAskAi();
                  window.dispatchEvent(new CustomEvent("luxsova:open-shopper"));
                }}
                className="btn-outline text-xs !py-2 !px-4 border-pink text-pink"
                data-testid="mobile-ask-ai-link"
              >
                Ask AI
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp text-xs !py-2 !px-4"
              >
                WhatsApp
              </a>
            </div>

            <div className="mt-4" data-testid="mobile-menu-install-slot">
              <InstallAppButton variant="menu" onAfterClick={() => setMenuOpen(false)} />
            </div>

            {/* Recently viewed strip — surfaces the last 5 products the
                shopper looked at so they can jump back in one tap. */}
            {recent.length > 0 && (
              <>
                <p className="overline text-pink mt-6 mb-3">Recently viewed</p>
                <div
                  className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-2 mb-4"
                  data-testid="mobile-recent-strip"
                >
                  {recent.map((p) => (
                    <Link
                      key={`rv-${p.slug}`}
                      to={`/product/${p.slug}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex-shrink-0 w-20"
                      data-testid={`mobile-recent-${p.slug}`}
                    >
                      <div className="w-20 h-24 bg-bg-2 overflow-hidden rounded p-1.5 flex items-center justify-center">
                        {p.image ? (
                          <SmartImage
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-xl font-extrabold text-pink/30">
                            {(p.name || "?").charAt(0)}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-ink-soft mt-1 line-clamp-2 leading-tight">
                        {p.name}
                      </p>
                    </Link>
                  ))}
                </div>
              </>
            )}

            <p className="overline text-pink mt-6 mb-3">Categories</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
              {CAT_TABS.map((c) => (
                <li key={`m-tab-${c.testId}`}>
                  <Link
                    to={c.to}
                    onClick={() => setMenuOpen(false)}
                    className="text-[15px] font-semibold text-ink hover:text-pink py-1 block"
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="overline text-pink mb-3">Women Collections</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
              {women.map((c) => (
                <li key={`w-${c.slug}`}>
                  <Link
                    to={`/category/${c.slug}`}
                    onClick={() => setMenuOpen(false)}
                    className="text-[14px] text-ink-soft hover:text-pink block py-0.5"
                    data-testid={`mobile-cat-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="overline text-pink mb-3">Men&apos;s Collections</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-10">
              {men.map((c) => (
                <li key={`m-${c.slug}`}>
                  <Link
                    to={`/category/${c.slug}`}
                    onClick={() => setMenuOpen(false)}
                    className="text-[14px] text-ink-soft hover:text-pink block py-0.5"
                    data-testid={`mobile-mcat-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
